// Deep import of the notifications module (not the `components-alpha` barrel) so the legacy
// adapter doesn't drag the entire alpha UI surface into every legacy module that uses
// `useNotification`.
import type { AddNotificationInput, AppNotification } from '@shared/lib/ui/components-alpha/useNotifications';

import type { IMessageBarMessage } from '../models/components.models';
import { toMessageBarType, toToastType } from './toastAdapter';

/**
 * Convert a legacy notification into the new context's `addNotification` input. The full legacy
 * message is stashed in `meta` so it can be reconstructed faithfully (preserving `messageBarType`,
 * `groupId`, `onClick`, `styles`, … for the legacy `MessageBarList` UI).
 */
export const toV2NotificationInput = (m: IMessageBarMessage): AddNotificationInput => ({
  id: m.id,
  type: toToastType(m.messageBarType),
  title: typeof m.title === 'string' ? m.title : undefined,
  message: m.message,
  data: m.data,
  onClick: m.onClick && ((notification) => m.onClick?.(undefined as never, toLegacyNotification(notification))),
  meta: m,
});

/** Reconstruct a legacy `IMessageBarMessage` from a unified notification for `MessageBarList`. */
export const toLegacyNotification = (n: AppNotification): IMessageBarMessage => {
  const base = (n.meta as IMessageBarMessage | undefined) ?? {
    message: n.message,
    title: n.title,
    data: n.data as IMessageBarMessage['data'],
    // Notifications added through the new store carry a new-style `onClick(notification)`; wrap it
    // into the legacy `(event, message)` slot the legacy list invokes.
    onClick: n.onClick && (() => n.onClick?.(n)),
  };
  return {
    ...base,
    id: n.id,
    read: n.read,
    messageBarType: base.messageBarType ?? toMessageBarType(n.type),
  };
};

/** Read the legacy `groupId` off a unified notification's stashed meta, if any. */
export const getNotificationGroupId = (n: AppNotification): string | undefined =>
  (n.meta as IMessageBarMessage | undefined)?.groupId;
